import './DSAuth/base.css';
import './DSAuth/explicitAuth.css';
import './DSAuth/authenticationLarge.css';
import './DSAuth/authenticationSmall.css';
import * as React from 'react';
import { t } from '@citrite/translate';
import {
	Button,
	errorMessageModal,
	withModals,
	WithModalsProps,
} from '@citrite/web-ui-component';
import { WorkspaceConfiguration } from '@citrite/workspace-ui-platform';
import { trackAnalyticsEvent, trackEvent } from 'analytics';
import $ from 'jquery';
import { isEqual } from 'lodash';
import { RouteComponentProps } from 'react-router-dom';
import URI from 'urijs';
import { FullscreenCard } from 'Components/FullscreenCard';
import { performance } from 'Components/performance';
import { useConfigurationContext } from 'Configuration/useConfigurationContext';
import { environment } from 'Environment';
import {
	offlineParameter,
	shouldUseLegacyOfflineFlow,
} from 'Environment/BrowserExtension/offlineParametersUtils';
import { clientType, getClientType } from 'Environment/getClientType';
import { hasUIProperty, UIProperty } from 'Environment/hasUIProperty';
import {
	ErrorHints,
	isStoreFrontFallbackEnabled,
	StoreFrontFallbackErrorPage,
} from 'ErrorPage/StoreFrontFallback';
import { getFromLocalStorage } from 'javascript/sf/Storage';
import { isHTML5ShieldEnabled } from 'LeaseWorkflow/ShieldModule';
import { resumeFormsToken } from 'Login/constants';
import { isWebViewLogon } from 'Login/DSAuth/authentication';
import { FormActions } from 'Login/DSAuth/formActions';
import { NextLogonMethodButton } from 'Login/DSAuth/nextLogonMethodButton';
import { LoginMessage } from 'Login/LoginMessage';
import { offlineModal } from 'Login/OfflineModal';
import {
	cleanupPendingLogins,
	getPendingLogins,
	PendingLogin,
	removePendingLogins,
} from 'Login/PendingLogin';
import { setPreviousLogoutExplicit } from 'Logout/previousLogoutConfig';
import {
	BrowserExtensionContext,
	ExtensionConfigurationState,
} from 'Workspace/BrowserExtension/Context';
import { markToLoadInOfflineMode } from 'Workspace/BrowserExtension/ResiliencyBrowserExtensionSupport';
import { useBrowserExtension } from 'Workspace/BrowserExtension/useBrowserExtension';
import { ChromeAppContext } from 'Workspace/ChromeApp/ChromeAppContext';
import { isChromeAppCompatible } from 'Workspace/ChromeApp/ChromeAppHelperMethods';
import { useChromeApp } from 'Workspace/ChromeApp/useChromeApp';
import { InitialLoading } from 'Workspace/InitialLoading';
import { browserExtensionAnalyticsReporter } from 'Workspace/TelemetryEvents/browserExtension/createBrowserExtensionAnalyticsReporter';
import { lastUserDetailsKey } from 'Workspace/UserContext/constants';
import { AuthenticationController } from './DSAuth/authenticationController';

export type Props = RouteComponentProps<null> & {
	browserExtensionContext: BrowserExtensionContext;
	chromeAppContext: ChromeAppContext;
	workspaceConfiguration: WorkspaceConfiguration;
};

export interface InformationMessage {
	title?: string;
	content?: React.ReactNode;
}

export interface AuthScreen {
	content?: React.ReactNode;
}

interface State {
	loading: boolean;
	infoMessage?: InformationMessage;
	storeFrontFallbackHint?: ErrorHints;
	authScreen?: AuthScreen;
}

class _Login extends React.Component<Props & WithModalsProps, State> {
	private explicitAuthRoot = React.createRef<HTMLElement>();
	private changePasswordRoot = React.createRef<HTMLElement>();
	private onPremCommonAuthRoot = React.createRef<HTMLElement>();
	private readonly attemptsBeforeAuthFailurePrompt = 1;
	private allowAccessToOfflineResourcesChromeApp = false;
	public state: State = {
		loading: true,
	};

	public render() {
		if (this.state.storeFrontFallbackHint) {
			return <StoreFrontFallbackErrorPage hint={this.state.storeFrontFallbackHint} />;
		}
		const showFormUI = !this.state.loading && !this.state.infoMessage;

		return (
			<>
				{this.state.loading && <InitialLoading />}
				{this.state.infoMessage && (
					<LoginMessage title={this.state.infoMessage.title}>
						{this.state.infoMessage.content}
					</LoginMessage>
				)}
				<div style={{ display: showFormUI ? 'block' : 'none' }}>
					<FullscreenCard
						ref={this.explicitAuthRoot}
						id="explicit-auth-screen"
						className="scrollable x-scrollable fullscreen-pane web-screen"
						centerContent
					>
						<div className="logon-area">
							<div className="form-content">
								<div className="form-container" />
								<NextLogonMethodButton
									id="linkSwitchLogonMethod"
									style={{ display: 'none' }}
								>
									{t('Workspace:login.switch_to_generic')}
								</NextLogonMethodButton>
								<div className="explicit-auth-progress">
									<div className="spinner authentication-spinner" />
								</div>
							</div>
						</div>
					</FullscreenCard>
					<FullscreenCard
						ref={this.changePasswordRoot}
						id="password-expiry-warning-screen"
						className="auth-password-expiry-view scrollable x-scrollable fullscreen-pane web-screen"
					>
						<div className="logon-area">
							<div
								className="authentication-content"
								role="alertdialog"
								aria-describedby="changePasswordHeader"
							>
								<h1 className="authentication-title main-text" id="changePasswordHeader">
									{t('Workspace:login.change_password_title')}
								</h1>
								<p className="authentication-message detail-text" />
								<div className="buttonscontainer">
									<Button.Wrapper id="btnPasswordChange" className="stacked-wrapper">
										<div className="default stacked button">
											{t('Workspace:login.change_password_now')}
										</div>
									</Button.Wrapper>
									<Button.Wrapper id="btnPasswordContinue" className="stacked-wrapper">
										<div className="stacked button">
											{t('Workspace:login.change_password_later')}
										</div>
									</Button.Wrapper>
								</div>
							</div>
						</div>
					</FullscreenCard>
					<FullscreenCard
						ref={this.onPremCommonAuthRoot}
						className="scrollable x-scrollable fullscreen-pane web-screen"
						centerContent
					>
						{this.state.authScreen && this.state.authScreen.content}
					</FullscreenCard>
				</div>
			</>
		);
	}

	private showForms = (authScreen?: AuthScreen) => {
		this.setState({ loading: false, authScreen });
	};

	private showMessage = (infoMessage: InformationMessage) => {
		this.setState({
			loading: false,
			infoMessage,
		});
	};

	public componentDidMount() {
		if (this.props.workspaceConfiguration) {
			this.authenticate();
		}
	}

	public componentDidUpdate(prevProps: Props) {
		if (!isEqual(this.props.workspaceConfiguration, prevProps.workspaceConfiguration)) {
			this.authenticate();
		}
	}

	private shouldShowStoreFrontFallback(pendingLogins: PendingLogin[]) {
		if (
			pendingLogins.length > this.attemptsBeforeAuthFailurePrompt &&
			isStoreFrontFallbackEnabled()
		) {
			return true;
		}
		return false;
	}

	private supportsWebOfflineMode = () => {
		return (
			hasUIProperty(
				this.props.workspaceConfiguration,
				UIProperty.AdvancedWorkspaceResiliency
			) &&
			this.props.browserExtensionContext.isSupportedCustomerRuntime &&
			getClientType() !== clientType.html5
		);
	};

	private shouldOpenOfflinePrompt(pendingLogins: PendingLogin[]) {
		// Note: UserID from the previously logged in user is required for offline
		// assets to be accessed in the cache. It's an accepted risk in the Shield
		// initiative that the user currently logging in may not be the same.
		const userHasLoggedInBefore = !!getFromLocalStorage(lastUserDetailsKey);
		if (
			userHasLoggedInBefore &&
			pendingLogins.length > this.attemptsBeforeAuthFailurePrompt
		) {
			return true;
		}
		return false;
	}

	private redirect(pendingLogin: PendingLogin) {
		const targetLocation = pendingLogin.locationAfterLogin;
		if (isWebViewLogon() || !environment.citrixCloudConnected) {
			this.props.history.push(targetLocation);
		} else {
			const clientRouteDestination = new URI(targetLocation.pathname)
				.search(targetLocation.search)
				.toString();
			const path = IS_ON_PREM ? '' : '/';
			const redirectUrl = new URI(path).hash(clientRouteDestination).toString();
			location.assign(redirectUrl);
		}
	}

	private cleanupExtraLoginAttempts(pendingLogins: PendingLogin[]) {
		if (pendingLogins.length > this.attemptsBeforeAuthFailurePrompt) {
			cleanupPendingLogins();
		}
	}

	private showOfflinePage(pendingLogin: PendingLogin) {
		environment.setCitrixCloudConnectedStatus(false);
		removePendingLogins();
		this.redirect(pendingLogin);
	}

	private async shouldShowOfflinePage(
		extensionConfiguration: ExtensionConfigurationState
	) {
		if (
			shouldUseLegacyOfflineFlow(
				extensionConfiguration,
				this.props.workspaceConfiguration
			)
		) {
			return await offlineModal();
		}

		return offlineParameter.isWsuiLaunchedInOfflineMode();
	}

	private async authenticate() {
		const pendingLogins = getPendingLogins();
		const latestPendingLogin = pendingLogins[0];
		if (!latestPendingLogin) {
			this.props.history.push('/');
			return;
		}

		if (this.supportsWebOfflineMode() && this.shouldOpenOfflinePrompt(pendingLogins)) {
			const extensionConfiguration =
				await this.props.browserExtensionContext.getExtensionConfiguration();
			if (
				extensionConfiguration.isActive &&
				extensionConfiguration.allowAccessToOfflineResources
			) {
				this.props.browserExtensionContext.notifyAuthenticationTimer('stop');

				if (await this.shouldShowOfflinePage(extensionConfiguration)) {
					// This is a marker captured in local storage to indicate that the user wants to load WSUI in offline mode
					// Which ideally should have been notified form BE to WSUI via URL parameter as it initiates the offline mode
					// As this mechanism is not available today, we are using this marker to load WSUI in offline mode
					// This we have to corrected back when the new implementation is available as part of WSUI-9433
					markToLoadInOfflineMode();
					trackAnalyticsEvent(
						browserExtensionAnalyticsReporter.getCloudOfflineEventWhenUserTriggered()
					);
					this.showOfflinePage(latestPendingLogin);
					return;
				}
			}
		}

		if (
			!this.supportsWebOfflineMode() &&
			this.shouldShowStoreFrontFallback(pendingLogins)
		) {
			this.setState({
				storeFrontFallbackHint: ErrorHints.UnknownAuthFailure,
			});
			removePendingLogins();
			return;
		}

		this.props.browserExtensionContext.notifyAuthenticationTimer('start');

		if (isChromeAppCompatible(this.props.workspaceConfiguration)) {
			this.props.chromeAppContext.notifyAuthenticationTimer('start');
			const chromeAppConfiguration =
				await this.props.chromeAppContext.getChromeAppConfiguration();
			this.allowAccessToOfflineResourcesChromeApp =
				chromeAppConfiguration.isActive &&
				chromeAppConfiguration.allowAccessToOfflineResources;
		}

		const authController = new AuthenticationController(
			$(this.explicitAuthRoot.current),
			$(this.changePasswordRoot.current),
			$(this.onPremCommonAuthRoot.current),
			{
				onError: (failureId?: string) => {
					const isSessionTimeout = failureId === 'authsessiontimeout';
					const isAthenaBrokedown = failureId === 'athenabrokedown';

					if (isSessionTimeout) {
						reloadAfterFatalError();
						return;
					}

					if (
						isAthenaBrokedown &&
						(this.allowAccessToOfflineResourcesChromeApp ||
							isHTML5ShieldEnabled(this.props.workspaceConfiguration))
					) {
						if (this.allowAccessToOfflineResourcesChromeApp) {
							this.props.chromeAppContext.notifyAuthenticationTimer('stop');
						}
						trackEvent('CloudOffline_AthenaFails');
						this.showOfflinePage(latestPendingLogin);
						return;
					}

					if (isStoreFrontFallbackEnabled()) {
						FormActions.resetDSAuthStyling();
						this.setState({
							storeFrontFallbackHint: ErrorHints.AuthenticationFailure,
						});
						return;
					}

					errorMessageModal({
						message: t('Workspace:login.terminal_error'),
						onCloseSuccess: reloadAfterFatalError,
						onDismiss: reloadAfterFatalError,
					});
				},
				onComplete: () => {
					this.props.browserExtensionContext.notifyAuthenticationTimer('stop');
					this.props.chromeAppContext.notifyAuthenticationTimer('stop');
					performance.loggedIn();
					removePendingLogins();
					setPreviousLogoutExplicit(false);
					this.redirect(latestPendingLogin);
				},
			},
			this.props.workspaceConfiguration,
			this.showForms,
			this.showMessage,
			this.props.location.search
		);
		this.cleanupExtraLoginAttempts(pendingLogins);
		authController.authenticate(latestPendingLogin.challenge);
	}
}

function reloadAfterFatalError() {
	cleanupStateFromAttemptedLogin();
	location.reload();
}

function cleanupStateFromAttemptedLogin() {
	if (location.hash.includes(resumeFormsToken)) {
		location.hash = '';
	}
}

const LoginWithModals = withModals(_Login);

export const Login: React.FC<RouteComponentProps<null>> = props => {
	const { workspaceConfiguration } = useConfigurationContext();
	const browserExtensionContext = useBrowserExtension();
	const chromeAppContext = useChromeApp();
	return (
		<LoginWithModals
			{...props}
			workspaceConfiguration={workspaceConfiguration}
			browserExtensionContext={browserExtensionContext}
			chromeAppContext={chromeAppContext}
		/>
	);
};
